import { memo, useMemo } from "react";
import { EdgeProps, EdgeLabelRenderer } from "@xyflow/react";

function stableSide(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 33 + seed.charCodeAt(i)) >>> 0;
  }
  return hash % 2 === 0 ? 1 : -1;
}

function ADEdgeComponent({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  label,
  data,
  style,
}: EdgeProps) {
  const graphSize = (data?.graphSize as number) ?? 0;
  const showLabelData = Boolean(data?.showLabel);

  const {
    path,
    arrowPath,
    midX,
    midY,
    showLabel,
    strokeWidth,
    arrowStrokeWidth,
    labelFontSize,
  } = useMemo(() => {
    const dx = targetX - sourceX;
    const dy = targetY - sourceY;
    const len = Math.hypot(dx, dy) || 1;

    const ux = dx / len;
    const uy = dy / len;
    const nx = -uy;
    const ny = ux;

    // Curvature is gentler at scale so neighboring lines don't look chaotic.
    const curveFactor = graphSize > 200 ? 0.025 : graphSize > 80 ? 0.035 : 0.045;
    const maxCurve = graphSize > 200 ? 14 : graphSize > 80 ? 18 : 24;
    const curvature = len > 140 ? Math.min(maxCurve, len * curveFactor) * stableSide(id) : 0;
    const cx = (sourceX + targetX) / 2 + nx * curvature;
    const cy = (sourceY + targetY) / 2 + ny * curvature;

    const path =
      curvature === 0
        ? `M ${sourceX} ${sourceY} L ${targetX} ${targetY}`
        : `M ${sourceX} ${sourceY} Q ${cx} ${cy} ${targetX} ${targetY}`;

    // Arrow head — back the tip off the target node so it doesn't overlap.
    const tipBackoff = graphSize > 200 ? 22 : graphSize > 80 ? 28 : 36;
    const arrowSize = graphSize > 200 ? 9 : graphSize > 80 ? 12 : 16;
    const tipX = targetX - ux * tipBackoff;
    const tipY = targetY - uy * tipBackoff;

    const arrowPath = [
      `M ${tipX} ${tipY}`,
      `L ${tipX - ux * arrowSize + nx * arrowSize * 0.6} ${tipY - uy * arrowSize + ny * arrowSize * 0.6}`,
      `M ${tipX} ${tipY}`,
      `L ${tipX - ux * arrowSize - nx * arrowSize * 0.6} ${tipY - uy * arrowSize - ny * arrowSize * 0.6}`,
    ].join(" ");

    const showLabel = Boolean(showLabelData && label && len > 160);

    return {
      path,
      arrowPath,
      midX: curvature === 0 ? (sourceX + targetX) / 2 : cx,
      midY: curvature === 0 ? (sourceY + targetY) / 2 : cy,
      showLabel,
      strokeWidth: graphSize > 500 ? 1.5 : graphSize > 200 ? 2 : graphSize > 80 ? 2.5 : 3,
      arrowStrokeWidth: graphSize > 500 ? 1.5 : graphSize > 200 ? 2 : graphSize > 80 ? 2.4 : 2.8,
      labelFontSize: graphSize > 80 ? 18 : graphSize > 30 ? 24 : 30,
    };
  }, [id, sourceX, sourceY, targetX, targetY, label, showLabelData, graphSize]);

  // Slightly fade lines in massive graphs so the canvas reads cleanly.
  const strokeOpacity = graphSize > 500 ? 0.35 : graphSize > 200 ? 0.45 : 0.6;

  return (
    <>
      <path
        id={id}
        d={path}
        style={{
          ...style,
          fill: "none",
          strokeWidth,
          stroke: `hsl(var(--muted-foreground) / ${strokeOpacity})`,
          strokeLinecap: "round",
        }}
      />

      <path
        d={arrowPath}
        style={{
          fill: "none",
          strokeWidth: arrowStrokeWidth,
          stroke: `hsl(var(--muted-foreground) / ${Math.min(0.85, strokeOpacity + 0.2)})`,
          strokeLinecap: "round",
          strokeLinejoin: "round",
        }}
      />

      {showLabel && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${midX}px, ${midY}px)`,
              pointerEvents: "none",
              fontSize: `${labelFontSize}px`,
            }}
            className="px-3 py-1 rounded bg-card/70 text-muted-foreground font-medium"
          >
            {label as string}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

export const ADEdge = memo(ADEdgeComponent);

ADEdge.displayName = "ADEdge";
